import { useQueries, useQuery } from "@tanstack/react-query"
import { Users, X } from "lucide-react"
import { type ReactNode, useState } from "react"
import { Link } from "react-router-dom"
import {
	type AudienceFilter,
	type AudienceSegment,
	contextChips,
	contextOf,
	plural,
	SEGMENT_KEYS,
} from "@/entities/audience"
import { draftCache } from "@/entities/bot-builder"
import { getTagStats, statsKeys } from "@/entities/bot-stats"
import { broadcastKeys, countBroadcastRecipients, totalOf } from "@/entities/broadcast"
import { cn } from "@/shared/lib/utils"
import { AnimatedNumber } from "@/shared/ui/animated-number"
import { Button } from "@/shared/ui/button"
import { LoadSwap } from "@/shared/ui/load-swap"
import { SegmentedControl } from "@/shared/ui/segmented-control"
import { AUDIENCE_PRESETS, groupOf, presetOf } from "../lib/audience-presets"
import { ChoiceCards } from "./choice-cards"
import { RecipientsPicker } from "./recipients-picker"
import { Section } from "./section"

type Mode = "ready" | "groups" | "custom" | "manual"

const MODES = [
	{ value: "ready", label: "Готовые группы" },
	{ value: "groups", label: "Группы бота" },
	{ value: "custom", label: "Свои условия" },
	{ value: "manual", label: "Конкретные люди" },
] as const satisfies readonly { value: Mode; label: string }[]

/** Условия фильтра одним сегментом; старое одиночное условие по переменной становится правилом. */
function segmentOf(filter: AudienceFilter): AudienceSegment {
	const segment: AudienceSegment = {}
	for (const key of SEGMENT_KEYS) {
		if (filter[key] !== undefined) Object.assign(segment, { [key]: filter[key] })
	}
	const legacy =
		filter.variable && filter.op
			? [{ variable: filter.variable, op: filter.op, value: filter.value }]
			: []
	const rules = [...legacy, ...(filter.variableRules ?? [])]
	return rules.length > 0 ? { ...segment, variableRules: rules } : segment
}

const modeOf = (filter: AudienceFilter): Mode =>
	filter.sessionIds !== undefined
		? "manual"
		: presetOf(filter)
			? "ready"
			: groupOf(filter)
				? "groups"
				: "custom"

/**
 * Кому уйдёт рассылка. Чаще всего это готовая группа («не подписаны на канал», «новые за
 * неделю») или группа из сценария бота: менеджер выбирает карточку с числом людей, а не
 * собирает условия. Свои условия и ручной выбор людей остаются для редких случаев.
 */
export function AudienceSection({
	botId,
	filter,
	onFilter,
	recipients,
	counting = false,
	filterDescription,
	tags = [],
	renderSegmentFilters,
	savedFilters,
}: {
	botId: string
	filter: AudienceFilter
	onFilter: (filter: AudienceFilter) => void
	recipients: number | undefined
	/** Число пересчитывается под новый фильтр: старое остаётся, но приглушено. */
	counting?: boolean
	filterDescription: string
	tags?: { id: string; name: string; archivedAt?: string | null }[]
	renderSegmentFilters: (
		value: AudienceSegment,
		onChange: (next: AudienceSegment) => void,
		hasContext: boolean,
	) => ReactNode
	/** Сохранённые выборки плашками над своими условиями. */
	savedFilters?: ReactNode
}) {
	const [mode, setMode] = useState<Mode>(() => modeOf(filter))

	// Фильтры готовых групп считаются один раз: дата «новых за неделю» иначе менялась бы на
	// каждой отрисовке, а вместе с ней ключ запроса числа людей.
	// Выбранная при открытии группа берёт свой сохранённый фильтр.
	const [presets] = useState(() =>
		AUDIENCE_PRESETS.map((preset) => ({
			...preset,
			value: preset.matches(filter) ? filter : preset.filter(),
		})),
	)
	const presetCounts = useQueries({
		queries: presets.map((preset) => ({
			queryKey: broadcastKeys.count(botId, preset.value),
			queryFn: () => countBroadcastRecipients(botId, preset.value),
			staleTime: 60_000,
			enabled: mode === "ready",
		})),
	})
	const tagStats = useQuery({
		queryKey: statsKeys.tags(botId),
		queryFn: () => getTagStats(botId),
		enabled: mode === "groups",
		staleTime: 60_000,
	})
	const tagCounts = new Map(tagStats.data?.tags.map((row) => [row.tagId, row.count]))
	const groups = draftCache
		.pickableTags(tags, filter.tagId ? [filter.tagId] : [])
		.sort((a, b) => (tagCounts.get(b.id) ?? 0) - (tagCounts.get(a.id) ?? 0))

	const switchMode = (next: Mode) => {
		setMode(next)
		if (next === "manual") onFilter({ sessionIds: [] })
		if (next === "ready" && !presetOf(filter)) onFilter(presets[0]?.value ?? {})
		if (next === "groups" && !groupOf(filter) && groups[0]) onFilter({ tagId: groups[0].id })
		if (next === "custom" && filter.sessionIds !== undefined) onFilter({})
	}

	const selectedPreset = presets.find((preset) => preset.matches(filter))?.id

	return (
		<Section
			title="Кому"
			hint="Список соберётся в момент отправки. Кто заблокировал бота, сообщение не получит."
		>
			<SegmentedControl
				aria-label="Как выбрать получателей"
				options={MODES}
				value={mode}
				onChange={(next) => next && switchMode(next)}
			/>
			<LoadSwap loading={false} id={mode}>
				{mode === "ready" ? (
					<ChoiceCards
						label="Готовые группы"
						value={selectedPreset}
						onChange={(id) => {
							const preset = presets.find((item) => item.id === id)
							if (preset) onFilter(preset.value)
						}}
						choices={presets.map((preset, index) => ({
							id: preset.id,
							label: preset.label,
							hint: preset.hint,
							count: totalOf(presetCounts[index]?.data),
						}))}
					/>
				) : mode === "groups" ? (
					groups.length > 0 ? (
						<ChoiceCards
							label="Группы бота"
							value={groupOf(filter)}
							onChange={(tagId) => onFilter({ tagId })}
							choices={groups.map((tag) => ({
								id: tag.id,
								label: tag.name,
								count: tagStats.data ? (tagCounts.get(tag.id) ?? 0) : undefined,
							}))}
						/>
					) : (
						<div className="flex flex-wrap items-center gap-3 rounded-xl border border-dashed border-border p-4 text-sm text-muted-foreground">
							<Users className="size-5 shrink-0" />
							<p className="min-w-0 flex-1">
								В сценарии бота пока нет групп. Их добавляет блок воронки или кнопка «Группа» в
								списке пользователей.
							</p>
							<Button asChild variant="outline" size="sm" className="rounded-full">
								<Link to={`/admin/bots/${botId}/analytics/groups`}>Открыть группы</Link>
							</Button>
						</div>
					)
				) : mode === "manual" ? (
					<RecipientsPicker
						botId={botId}
						value={filter.sessionIds ?? []}
						onChange={(sessionIds) => onFilter({ sessionIds })}
					/>
				) : (
					<div className="space-y-3">
						{savedFilters}
						{contextChips(filter, tags).length > 0 ? (
							<div className="flex flex-wrap gap-2">
								{contextChips(filter, tags).map((chip) => (
									<span
										key={chip.key}
										className="inline-flex h-8 items-center gap-1 rounded-full border border-primary/40 bg-primary/10 pr-0.5 pl-3 text-sm"
									>
										{chip.label}
										<button
											type="button"
											aria-label={`Убрать условие «${chip.label}»`}
											className="grid size-6 place-items-center rounded-full text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
											onClick={() => onFilter({ ...filter, [chip.key]: undefined })}
										>
											<X className="size-3.5" />
										</button>
									</span>
								))}
							</div>
						) : null}
						{renderSegmentFilters(
							segmentOf(filter),
							(segment) => onFilter({ ...contextOf(filter), ...segment }),
							contextChips(filter).length > 0,
						)}
					</div>
				)}
			</LoadSwap>
			<div className="flex items-baseline justify-between gap-3 border-t border-border pt-4">
				<LoadSwap loading={false} id={filterDescription} autoHeight={false} className="min-w-0">
					<p className="text-sm text-muted-foreground">{filterDescription}</p>
				</LoadSwap>
				<div
					className={cn(
						"shrink-0 text-sm whitespace-nowrap transition-opacity duration-200",
						counting && "opacity-50",
					)}
				>
					<LoadSwap loading={recipients === undefined} autoHeight={false} className="inline-block">
						{recipients === undefined ? (
							<span className="text-muted-foreground">Считаем…</span>
						) : (
							<>
								<AnimatedNumber value={recipients} className="font-semibold tabular-nums" />{" "}
								{plural(recipients, ["получатель", "получателя", "получателей"])}
							</>
						)}
					</LoadSwap>
				</div>
			</div>
		</Section>
	)
}
